/**
 * Utility per verificare lo stato dei pagamenti delle commissioni
 * Usata per bloccare la modifica delle commissioni già pagate
 */

import { supabase } from '../supabase';

export type ConsultantPaymentStatus = 'unpaid' | 'partial' | 'full';

export interface CommissionPaymentInfo {
  dealId: string;
  serviceId: string | null;
  variableId: string | null;
  personId: string;
  personType: 'agent' | 'referrer';
  monthIndex: number | null;
  status: ConsultantPaymentStatus;
  totalPaid: number;
  commissionAmount: number;
}

/**
 * Verifica se una commissione è bloccata (ha pagamenti)
 * @param status Lo stato del pagamento della commissione
 * @returns true se la commissione è bloccata (partial o full)
 */
export function isCommissionLocked(status: ConsultantPaymentStatus | string | null | undefined): boolean {
  return status === 'partial' || status === 'full';
}

/**
 * Verifica se un deal ha commissioni con pagamenti
 * @param dealId ID del deal
 * @param organizationId ID dell'organizzazione
 * @returns true se esistono commissioni con pagamenti per questo deal
 */
export async function hasCommissionPayments(
  dealId: string,
  organizationId: string
): Promise<boolean> {
  // Prima verifica: esistono effettivamente pagamenti in commission_consultant_payments?
  // Questo previene falsi positivi quando consultant_payment_status è "sporco"
  const { data: payments, error: paymentsError } = await supabase
    .from('commission_consultant_payments')
    .select('id, amount_paid, paid_at')
    .eq('deal_id', dealId)
    .limit(5);

  if (paymentsError) {
    console.error('[commissionPaymentCheck] Error checking actual payments:', paymentsError);
    return false; // In caso di errore, non bloccare (fail-open)
  }

  console.log(`[commissionPaymentCheck] Deal ${dealId}: found ${payments?.length || 0} actual payments:`, payments);

  // Se non ci sono pagamenti effettivi nella tabella pagamenti, non bloccare
  if (!payments || payments.length === 0) {
    console.log(`[commissionPaymentCheck] Deal ${dealId}: NO payments found, NOT locking`);
    return false;
  }

  // Se ci sono pagamenti, verifica lo status in commission_calculations
  const { data, error } = await supabase
    .from('commission_calculations')
    .select('id, consultant_payment_status')
    .eq('deal_id', dealId)
    .eq('organization_id', organizationId)
    .in('consultant_payment_status', ['partial', 'full'])
    .limit(5);

  if (error) {
    console.error('[commissionPaymentCheck] Error checking payments:', error);
    return false; // In caso di errore, non bloccare (fail-open)
  }

  console.log(`[commissionPaymentCheck] Deal ${dealId}: found ${data?.length || 0} commission_calculations with paid status:`, data);

  return (data?.length || 0) > 0;
}

/**
 * Verifica se esistono commissioni pagate per una persona specifica in un deal
 * @param dealId ID del deal
 * @param personId ID della persona (user o referrer)
 * @param personType Tipo di persona ('agent' o 'referrer')
 * @param organizationId ID dell'organizzazione
 * @returns true se la persona ha commissioni con pagamenti per questo deal
 */
export async function hasPersonCommissionPayments(
  dealId: string,
  personId: string,
  personType: 'agent' | 'referrer',
  organizationId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('commission_calculations')
    .select('id')
    .eq('deal_id', dealId)
    .eq('person_id', personId)
    .eq('person_type', personType)
    .eq('organization_id', organizationId)
    .in('consultant_payment_status', ['partial', 'full'])
    .limit(1);

  if (error) {
    console.error('[commissionPaymentCheck] Error checking person payments:', error);
    return false;
  }

  return (data?.length || 0) > 0;
}

/**
 * Recupera tutte le commissioni con pagamenti per un deal
 * @param dealId ID del deal
 * @param organizationId ID dell'organizzazione
 * @returns Lista delle commissioni pagate con dettagli
 */
export async function getPaidCommissionsForDeal(
  dealId: string,
  organizationId: string
): Promise<CommissionPaymentInfo[]> {
  const { data, error } = await supabase
    .from('commission_calculations')
    .select('deal_id, service_id, variable_id, person_id, person_type, month_index, consultant_payment_status, total_paid_to_consultant, commission_amount')
    .eq('deal_id', dealId)
    .eq('organization_id', organizationId)
    .in('consultant_payment_status', ['partial', 'full']);

  if (error) {
    console.error('[commissionPaymentCheck] Error fetching paid commissions:', error);
    return [];
  }

  return (data || []).map(row => ({
    dealId: row.deal_id,
    serviceId: row.service_id,
    variableId: row.variable_id,
    personId: row.person_id,
    personType: row.person_type as 'agent' | 'referrer',
    monthIndex: row.month_index,
    status: row.consultant_payment_status as ConsultantPaymentStatus,
    totalPaid: row.total_paid_to_consultant || 0,
    commissionAmount: row.commission_amount || 0
  }));
}

/**
 * Messaggio di errore standard per commissioni bloccate
 */
export function getCommissionLockedMessage(status: ConsultantPaymentStatus | string): string {
  if (status === 'full') {
    return 'Questa commissione è stata completamente pagata e non può essere modificata.';
  }
  return 'Questa commissione ha pagamenti parziali registrati e non può essere modificata.';
}
